/**
 * @file dcp-client/worker/supervisor2/job-manager.js
 *
 * A support class for Supervisor2.
 * It is a wrapper for the job object returned from the requestTask,
 * along with tracking slices and sandboxes associated with the job. 
 *
 * @author Wes Garland, wes@distributive.network,
 *         Paul, paul@distributive.network,
 * @date Dec 2020,
 *       June 2022, Jan-April 2023
 * @module job-manager
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

// Turn on to see how metrics grow with time.
const displayStatHistory = false;

/** @typedef {string} opaqueId */ // 22 character base64 string
/** @typedef {import('./sandbox2').Sandbox} Sandbox                      */
/** @typedef {import('./sandbox2').SandboxHandle} SandboxHandle          */
/** @typedef {import('./index').Supervisor} Supervisor                   */
/** @typedef {import('dcp/utils/jsdoc-types').SliceMessage} SliceMessage */
/** @typedef {import('dcp/utils/jsdoc-types').Auth} Auth                 */
/** @typedef {import('dcp/utils/jsdoc-types').Signature} Signature       */
/** @typedef {import('dcp/utils/jsdoc-types').AuthMessage} AuthMessage   */
/** @typedef {import('dcp/dcp-client/worker/origin-access-manager').OriginAccessManager} OriginAccessManager */

const { assert } = require('dcp/common/dcp-assert');
const inspect = Symbol.for('nodejs.util.inspect.custom');
const debugging = require('dcp/debugging').scope('worker');
const EventEmitter = require('events');
const { DCPError } = require('dcp/common/dcp-error');
const { Synchronizer } = require('dcp/common/concurrency');
const { rehydrateRange } = require("dcp/dcp-client/range-object");
const { fetchURIAndLength } = require('dcp/utils/fetch-uri');
const utils = require('dcp/utils');
const { stringify } = utils;
const wd = require('dcp/utils/worker-and-daemons');
const { allowOriginsPurposes } = require('dcp/common/worker-constants');
const semver = require('semver');

const { capabilities } = require('dcp/common/scheduler-constants');
const { Slice } = require('./slice2');
const { Promise_any } = require('./promise_any');
const { SandboxError } = require('./sandbox2');
const common = require('./common');
const { selectiveDebug, selectiveDebug2, debugBuild, selectiveSupEx } = common;
const { getSerializerForJob } = require('dcp/dcp-client/job-values');

const INITIAL = 'initial';
const READY   = 'ready';
const STOP    = 'stop';   // UNUSED
const REFUSE  = 'refuse'; // UNUSED
const BROKEN  = 'broken'; // this.destroy(false) has been called
const DEAD    = 'dead';   // this.destroy(true) has been called

class BadOriginError   extends SandboxError { constructor(msg) { super('EPERM_ORIGIN', msg, 'BadOriginError'); } }
class RemoteFetchError extends SandboxError { constructor(msg) { super('EFETCH',       msg, 'RemoteFetchError'); } }

/**
 * Public event emitter.
 * Make data public so it works with introspection.
 * However, #jobManager is still private because nobody needs to see that.
 * https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
 */
class JobHandle extends EventEmitter
{
  /** @type {JobManager} */
  #jobManager;

  /**
   * @constructor
   * @param {JobManager} jobManager
   */
  constructor (jobManager)
  {
    super({ captureRejections: false });
    for (let prop of Object.keys(this)) /* hide enumerable own props created by super */
    {
      Object.defineProperty(this, prop, {
        value:        this[prop],
        writable:     true,
        configurable: true,
        enumerable:   false,
      });
    }

    this.#jobManager  = jobManager;
    /** @type {string} */
    this.id           = jobManager.jobId;
    /** @type {string} */
    this.name         = jobManager.public?.name ?? '<unknown>';
    /** @type {string} */
    this.description  = jobManager.public?.description ?? '<unknown>';
    /** @type {string} */
    this.link         = jobManager.public?.link ?? '<unknown>';
    /** @type {boolean} */
    this.hasGPU       = jobManager.hasGPU;
    /** @type {*} */
    this.capabilities = jobManager.supervisor.capabilities;
  }
  /** @type {boolean} */
  get estimation () { return this.#jobManager.isEstimation; }
  /** @type {{CPUTime: number, GPUTime: number, CPUDensity: number, GPUDensity: number, InDataSize: number, OutDataSize: number}} */
  get metrics ()
  {
    // If a local stat (e.g. localMetrics.CPUTime) is 0 we return metrics.sliceCPUTime from jobPerfData.
    // Similarly for GPUTime, CPUDensity, GPUDensity, InDataSize, OutDataSize ...
    return {
      CPUTime:    this.#jobManager.emaCPUTime,
      GPUTime:    this.#jobManager.emaGPUTime,
      CPUDensity: this.#jobManager.estimateCPUDensity,
      GPUDensity: this.#jobManager.estimateGPUDensity,
      InDataSize: this.#jobManager.localMetrics.InDataSize || this.#jobManager.metrics.sliceInDataSize,
      OutDataSize: this.#jobManager.localMetrics.OutDataSize || this.#jobManager.metrics.sliceOutDataSize,
    };
  }
}
exports.JobHandle = JobHandle;

//
// Index to functionality -- search for '_Idx' to toggle through the index.
//
// 1)  class JobManager
// 2)  addSlices, fetchDependencies, updateStatistics, update, predictLoad
// 3)  reserveOneSlice, resetSlices, destroy, destroyAll
// 4)  resolveWorktime, runSliceOnSandbox
// 5)  removeSlice
// 6)  fetchError, fetchJob, fetchSliceData
// 7)  countSliceStr, fetchThing, dumpSlices.
//

// _Idx
//
// class JobManager
// A JobManager handles all scheduling of slices for a given job.
// It's also responsible for fetching slice data, work functions and arguments.
// And it collects statistics about slice completion times and resource usage.
// All functionality across jobs is handled by Supervisor.
//

/**
 * JobManager Constructor. An instance of JobManager knows everything about a given
 * job within the supervisor, including:
 *  - work function code
 *  - work function arguments
 *  - all the SliceManager instances that go with this job
 *  - how long a slice of this job is expected to run
 *
 * Instances of JobManager emit the following events:
 *  - addSlice(sliceHandle)
 *  - deleteSlice(sliceHandle)
 *  - statusChange(new state, old state);
 *
 * JobManager States
 *
 * Start state:
 * initial
 *
 * Intermediate states:
 * ready
 *
 * Terminal states:
 * broken - job could not be initialized
 * refuse - for some reason, we have decided that we don't want work for this job
 * stop   - job manager has been stopped
 *
 * Valid transitions:
 * initial  -> broken
 * initial  -> ready
 * initial  -> ready -> stop
 * anything -> dead
 * 
 * NOTE: If you ever use a property with a leading underscore you are probably making a mistake.
 *       But if you must, please ask paul, yarn, bryan or eddie for a CR.
 */
class JobManager
{
  /** @type {Promise<*>} */
  #jobPromise;
  /** @type {boolean} */
  #worktimeResolved

  /**
   * @constructor
   * @param {Supervisor}     parent               - Owning Supervisor.
   * @param {object}         jobMessage           - Job Descriptor from getJobsForTask.
   * @param {SliceMessage[]} sliceMessages        - Messages from task distributor describing slices.
   * @param {AuthMessage}    authorizationMessage - The signature that shipped with the task authorizing this worker.
   */
  constructor(parent, jobMessage, sliceMessages, authorizationMessage)
  {
    /** @type {*} */ // Figure out the type
    this.jobMessage = { ...jobMessage };
    /** @type {string} */
    this.jobId = this.jobMessage.jobId;
    /** @type {Slice[]} */
    this.sliceInventory = []; // All slices for this.jobId.
    /**
     * Never call this.state.destroy().
     * Because Supervisor.cursor contains arrays of job managers and it is too difficult to design the
     * cursor to easily remove individual job managers, so we just leave BROKEN or DEAD job managers
     * until the cursor algorithm removes them. They will be removed from Supervisor.sandboxInventory
     * after the next successful fetch by calling clearUnusedJobs, since BROKEN or DEAD means they
     * are or will be empty.
     * @type {Synchronizer}
     */
    this.state = new Synchronizer(INITIAL, [ INITIAL, READY, STOP, REFUSE, BROKEN, DEAD ]);
     /** @type {Supervisor} */
    this.supervisor = parent;
    /** @type {{CPUTime: number, GPUTime: number, ElapsedTime: number, CPUDensity: number, GPUDensity: number, InDataSize: number, OutDataSize: number, CPUTime_variance: number, GPUTime_variance: number }} */
    this.localMetrics = null;
    /** @type {Array<{slice: number, reason: string|boolean, retries: number}>} */
    this.rejectedJobReasons = [];
    /** @type {boolean} */
    this.isEstimation = true;
    /** @type {boolean} */
    this.isLong = false;
    /** @type {number} */
    this.inputDataSize = 0;
    /** @type {boolean} */
    this.#worktimeResolved = false;
    /** @type {string} */
    this.jobCgId = this.jobMessage.jobCgId;
    /** @type {number} */
    this.fetchArtifactTime = 0;
    /** @type {number} */
    this.consecutiveSandboxAssignFailures = 0;

    this.#jobPromise = null;

    /*
     * Make sure this.metrics contains valid data.
     * With old legacy schedulers, this may not be so.
     */
    this.fixUpMetrics();

    if (displayStatHistory)
    {
      this.CPUHistory = [];
      this.CPUVarHistory = [];
    }

    /** 
     * Start loading dependencies in the background. Once these are loaded, this.state will
     * transition to 'ready' and the job will be ready for work. Do not place this job manager
     * in inventory until 'ready'.
     */
    this.#jobPromise = this.fetchDependencies(sliceMessages, authorizationMessage);

    /**
     * Check for GPU support.
     * @type {boolean}
     */
    this.hasGPU = Boolean(jobMessage.needCapabilities & (capabilities.environment.webgpu | capabilities.environment.offscreenCanvas));

    /**
     * Backwards compatibility for kvin v9 update. All new jobs include the KVINv9 bit set.
     * @type {any}
     */
    this.serializer = getSerializerForJob(jobMessage, capabilities);
    /**
     * Event emitter containing info that describes the job.
     * @type {JobHandle}
     */
    this.jobHandle = new JobHandle(this);

    /**
     * Emit the 'job' event on the worker event emitter.
     */
    this.supervisor.worker.emit('job', this.jobHandle);
  }

  /** @type {boolean} */
  get workerConsole () { return this.jobMessage.workerConsole; }
  /** @type {boolean} */
  get almostDone ()    { return this.jobMessage.almostDone; }
  /** @type {boolean} */
  get localExec ()     { return this.jobMessage.localExec; }

  // These 3 properties have type object.
  get mro ()          { return this.jobMessage.mro; }
  get arguments ()    { return this.jobMessage.arguments; }
  get workFunction () { return this.jobMessage.workFunction; }

  /** @type {{computeGroups: Array<{opaqueId: string, name: string, description: string}>, name: string, description: string, link: string}} */
  get public ()       { return this.jobMessage.public; }
  /** @type {{sliceCPUTime: number, sliceGPUTime: number, sliceCPUDensity: number, sliceGPUDensity: number, sliceInDataSize: number, sliceOutDataSize: number, lastSliceNumber: number, measuredSlices: number, alpha: number}} */
  get metrics ()      { return this.jobMessage.metrics; }

  /** @type {boolean} */
  get initial ()      { return this.state.is(INITIAL); }
  /** @type {boolean} */
  get ready ()        { return this.state.is(READY); }
  /** @type {boolean} */
  get dead ()         { return this.state.is(DEAD); }

  // These 3 aren't needed yet.
  ///** @type {boolean} */
  //get stopped () { return this.state.is(STOP); }
  ///** @type {boolean} */
  //get refuse ()  { return this.state.is(REFUSE); }
  ///** @type {boolean} */
  //get broken ()  { return this.state.is(BROKEN); }

  /** @type {Sandbox[]} */
  get sandboxInventory ()  { return this.supervisor.sandboxInventory.filter((sandbox) => sandbox.jobId === this.jobId && !sandbox.isTerminated); }
  /** @type {Sandbox[]} */
  get assignedSandboxes () { return this.supervisor.sandboxInventory.filter((sandbox) => sandbox.jobId === this.jobId && sandbox.isAssigned); }
  /** @type {Sandbox[]} */
  get workingSandboxes ()  { return this.supervisor.sandboxInventory.filter((sandbox) => sandbox.jobId === this.jobId && sandbox.isWorking); }
  /** @type {Slice[]} */
  get readySlices ()       { return this.sliceInventory.filter((slice) => slice.isReady); }
  /** @type {Slice[]} */
  get queuedSlices ()      { return this.sliceInventory.filter((slice) => slice.isQueued); } // Ready and soon-to-be-ready
  /** @type {Slice[]} */
  get reservedSlices ()    { return this.sliceInventory.filter((slice) => slice.isReserved); }
  /** @type {Slice[]} */
  get activeSlices ()      { return this.sliceInventory.filter((slice) => slice.isActive); } // Reserved, working, workdone and complete.
  /** @type {Slice[]} */
  get workingSlices ()     { return this.sliceInventory.filter((slice) => slice.isWorking || slice.isReserved); } // Working and soon-to-be-working
  /** @type {Slice[]} */
  get unFinishedSlices ()  { return this.sliceInventory.filter((slice) => !slice.isFinished); }

  /** @type {number} */
  get sliceMultiplier () { return this.supervisor.options.sliceMultiplier; }
  /** @type {number} */
  get sliceSigmas ()     { return this.supervisor.options.sliceSigmas; }
  /** @type {number} */
  get sliceBiasMs ()     { return this.supervisor.options.sliceBiasMs; }
  /** @type {number} */
  get lifetimeFactor ()  { return this.supervisor.options.lifetimeFactor; }

  /** @type {Promise<*>} */
  get jobPromise ()      { return this.#jobPromise; }
  /** @type {number} */
  get defaultCPUQuanta () { return this.isEstimation ? 1.0 : this.supervisor.quanta.CPU.defaultQuanta; }
  /** @type {number} */
  get defaultGPUQuanta () { return this.isEstimation ? 1.0 : this.supervisor.quanta.GPU.defaultQuanta; }
  /** @type {number} */
  get globalCPUTime ()    { return this.metrics?.sliceCPUTime || 0;  }
  /** @type {number} */
  get globalGPUTime ()    { return this.metrics?.sliceGPUTime || 0;  }
  /** @type {number} */
  get globalGPUDensity () { return this.hasGPU ? wd.globalGPUDensity(this.metrics) : 0;  }
  /** @type {number} */
  get globalElapsedTime () { return wd.globalElapsedTime(this.metrics) || this.supervisor.options.targetTaskDurationMs;  }
  /** @type {number} */
  get emaCPUTime ()     { return this.localMetrics?.CPUTime || (this.globalCPUTime * this.defaultCPUQuanta); }
  /** @type {number} */
  get emaGPUTime ()     { return this.localMetrics?.GPUTime || (this.globalGPUTime * this.defaultGPUQuanta); }
  /** @type {number} */
  get emaElapsedTime () { return this.localMetrics?.ElapsedTime || (this.globalElapsedTime * this.defaultCPUQuanta); }
  /** @type {number} */
  get adjElapsedTime () { return Math.min(this.emaElapsedTime, this.supervisor.options.targetTaskDurationMs * Math.min(1, this.defaultCPUQuanta)); }
  /** @type {string} */
  get identifier ()   { return `${this.jobId}.${this.state}`; }
  /** @type {string} */
  get sliceState ()   { return `${this.queuedSlices.length}.${this.workingSliceCount}.${this.sliceInventory.length}.${this.workRemaining()}`; }
  /** @type {string} */
  get [inspect] ()    { return `[Object JobManager <${this.public.name}::${this.jobId}::${this.state}>]`; }
  /** @type {OriginAccessManager} */
  get originManager() { return this.supervisor.originManager; }
  /**
   * Expected CPU density of a slice.
   * @type {number}
   */
  get estimateCPUDensity () { return this.localMetrics?.CPUDensity || wd.globalCPUDensity(this.metrics); }
  /** @type {number} */
  get estimateGPUDensity () { return this.localMetrics?.GPUDensity || this.globalGPUDensity; }
  /** @type {number} */
  get estimateDensity () { return Math.min(1, this.estimateCPUDensity + this.estimateGPUDensity); }
  
  /**
   * Are there no working slices?
   * @type {boolean}
   */
  get isNotWorking ()
  {
    for (const slice of this.sliceInventory)
      if (slice.isReserved || slice.isWorking)
        return false;
    return true;
  }
  /**
   * Estimated count of working (or reserved) slices.
   * @type {number}
   */
  get workingSliceCount ()
  {
    let count = 0;
    for (const slice of this.sliceInventory)
      if (slice.isReserved || slice.isWorking)
        ++count;
    return count;
  }
  /**
   * Estimated density of working (or reserved) slices.
   * @type {number}
   */
  get workingSliceDensity () { return this.workingSliceCount * this.estimateCPUDensity;  }
  /**
   * Estimated density of working (or reserved) slices.
   * @type {number}
   */
  get workingGPUDensity () { return this.workingSliceCount * this.estimateGPUDensity;  }
  /**
   * Create array of sliceNumbers from all unfinished slices.
   * @type {number[]}
   */
  get sliceNumbers ()
  {
    const result = [];
    for (const slice of this.sliceInventory)
      if (!slice.isFinished)
        result.push(slice.sliceNumber);
    return result;
  }
  /**
   * Always display max info under debug builds, otherwise maximal error
   * messages are displayed to the worker, only if both worker and client agree.
   * When displayMaxDiagInfo is true, display stack trace and other enhanced diag info.
   * @type {boolean}
   **/
  get displayMaxDiagInfo ()
  {
    return common.displayMaxDiagInfo() || this.workerConsole && this.supervisor.options.allowConsoleAccess;
  }

  // _Idx
  //
  // addSlices, update, returnDuplicateSlices, fetchDependencies, cloneSlice, isValid, fixUpMetrics,
  // updateStatistics, computeOverdueThreshold, workRemaining, predictLoad
  //

  /**
   * Add slices to the job manager's inventory.
   *
   * @param {SliceMessage[]} sliceMessages        - Messages from task distributor describing slices.
   * @param {AuthMessage}    authorizationMessage - The signature that shipped with the task authorizing this worker.
   * @returns {Promise<*>}
   */
  addSlices (sliceMessages, authorizationMessage)
  {
    /** @type {Array<Promise<void>>} */
    const slicePromises = [];
    sliceMessages.forEach((sliceMessage) => {
      const slice = new Slice(this, sliceMessage, authorizationMessage);
      selectiveDebug2() && console.debug(`JobManager.addSlices: worker ${this.supervisor.workerId}, slice ${slice.identifier}, isEstimation ${slice.isEstimation}, isLong ${slice.isLong}`);
      if (!slice.isEstimation)
        this.isEstimation = false;
      if (slice.isLong)
        this.isLong = true;
      this.sliceInventory.push(slice);
      const fetchSliceDataPromise = slice.fetchSliceDataPromise
        .catch((error) => {
          selectiveSupEx() && console.debug(`JobManager.addSlices: Failed fetching slice datum for sliceNumber ${slice.sliceNumber}, job ${this.jobId}`);
          if (error.code !== 'ESLICEFETCH' || this.localExec)
            this.supervisor.directJobError(slice, error, `failed fetching slice data for sliceNumber ${slice.sliceNumber}, job ${this.identifier}`);
          this.returnSlice(slice, common.errorCodeOrString(error, `failed fetching slice data: ${error.message}`));
        });
      slicePromises.push(fetchSliceDataPromise);
    });

    // Promise.any will reject an empty array which is possible because of returnDuplicateSlices.
    if (slicePromises.length < 1)
      slicePromises.push(Promise.resolve());

    return Promise_any(slicePromises);
  }

  /**
   * Update jobMessage, add some slices to inventory.
   * @param {object}         jobMessage             - Job Descriptor from getJobsForTask.
   * @param {SliceMessage[]} [sliceMessages]        - Messages from task distributor describing slices.
   * @param {AuthMessage}    [authorizationMessage] - The signature that shipped with the task authorizing this worker.
   * @returns {Promise<*>}
   */
  update (jobMessage, sliceMessages, authorizationMessage)
  {
    Object.assign(this.jobMessage, jobMessage);
    if (jobMessage?.worktime)
      this.#worktimeResolved = false;

    /*
     * Make sure this.metrics contains valid data.
     * With old legacy schedulers, this may not be so.
     */
    this.fixUpMetrics();
    selectiveDebug2() && console.debug('JM.update: new this.jobMessage', this.jobMessage.metrics);

    if (sliceMessages?.length > 0)
    {
      sliceMessages = this.returnDuplicateSlices(sliceMessages, authorizationMessage);
      return (this.#jobPromise = this.addSlices(sliceMessages, authorizationMessage));
    }
  }

  /**
   * Do not allow a new slice from TD when worker is working
   * (or finshined working but not submitted to RS yet) on the same slice.
   * @param {SliceMessage[]} sliceMessages        - Messages from task distributor describing slices.
   * @param {AuthMessage}    authorizationMessage - The signature that shipped with the task authorizing this worker.
   * @returns {SliceMessage[]}
   */
  returnDuplicateSlices (sliceMessages, authorizationMessage)
  {
    // Algo for filtering sliceMessages is roughly O(N^2) which is usually less than 1024,
    // so probably no perf concerns. Sorting and binary search would make it roughy O(N * Log(N)).
    /** @todo XXXpfr Think about better perf. */
    /** @todo XXXptr Allow a sliceMessage to be returned w/o turning it into a Slice. */
    /** @todo XXXpfr Merge this logic with a JobManager version of loadedAndReadyJobSlices  */
    // Do the 'merge with loadedAndReadyJobSlices' after the final Soteria MR gets in.
    // Then the scheduler-level loadedAndReadyJobSlices will simply concatenate the
    // corresponding JobManages arrays.

    // Find the unfinished sliceNumbers.
    const unFinishedSliceNumbers = [];
    for (const slice of this.sliceInventory)
      if (!slice.isFinished)
        unFinishedSliceNumbers.push(slice.sliceNumber);

    // Early return when there are no unfinished slices.
    if (unFinishedSliceNumbers.length < 1)
      return sliceMessages;

    // Make sure there's no intersection of unfinished slices with incoming slice messages.
    const filteredSliceMessages = [];
    for (const msg of sliceMessages)
      if (unFinishedSliceNumbers.indexOf(msg.sliceNumber) === -1)
        filteredSliceMessages.push(msg);
      else
      {
        /** XXXpfr @todo Allow a SliceMessage to be directly returned to scheduler. */
        const slice = new Slice(this, msg, authorizationMessage, false);
        this.supervisor.directReturnSlice(slice, 'return duplicate slice');
      }

    return filteredSliceMessages;
  }

  /**
   * Fetch dependencies.
   * @param {SliceMessage[]} sliceMessages        - Messages from task distributor describing slices.
   * @param {AuthMessage}    authorizationMessage - The signature that shipped with the task authorizing this worker.
   * @returns {Promise<*>}
   */
  async fetchDependencies (sliceMessages, authorizationMessage)
  {
    this.fetchArtifactTime = Date.now();
    return this.fetchJob(this.jobMessage)
      .then (() => {
        debugging('supervisor') && console.debug(`JobManager is transitioning to READY: ${this.identifier}; fetch artifact timespan ${Date.now() - this.fetchArtifactTime}; unresolved jobs`, this.supervisor.unresolvedJobs);
        this.state.set(INITIAL, READY);
        this.fetchArtifactTime = Date.now() - this.fetchArtifactTime; // So we can lookup artifact resolution timespan.
        this.supervisor.jobManagerInventory.push(this);
        if (!common.removeElement(this.supervisor.unresolvedJobs, this.jobId) && debugBuild)
          this.supervisor.warning(`Cannot find jobId ${this.jobId} in unresolvedJobs ${utils.stringify(this.supervisor.unresolvedJobs)}`);
        // Now resolve the slice data.
        return this.addSlices(sliceMessages, authorizationMessage);
      }, (error) => {
        selectiveSupEx() && console.error(`fetchJob has failed for job ${this.jobId}, fetch artifact timespan ${Date.now() - this.fetchArtifactTime}, errorCode ${error.code}, localExec ${this.localExec}, error.message ${error.message}, unresolved jobs`, this.supervisor.unresolvedJobs);
        this.state.set(INITIAL, BROKEN);

        // Insert all slices to the sliceInventory without downloading their data, so that
        // JobManager.destroy will return slices to scheduler.
        sliceMessages.forEach((sliceMessage) => {
          const slice = new Slice(this, sliceMessage, authorizationMessage, false);
          this.sliceInventory.push(slice);
        });

        // Only emit an error for a single slice.
        assert(this.sliceInventory.length > 0);
        if (error.code !== 'EJOBFETCH' && error.code !== 'ESLICEFETCH' || this.localExec)
          this.supervisor.directJobError(this.sliceInventory[0], error, `Failed to fetch dependencies for job ${this.jobId}`);

        // Clean up this jobManager instance, and soft-reject the job.
        // Soft-reject: return all slices and remove job references.
        // Hard-reject: soft-reject and add to this.rejectedJobs to prvent TD from fetching future slices.
        common.removeElement(this.supervisor.unresolvedJobs, this.jobId);
        assert(this.supervisor.jobManagerInventory.filter((jm) => jm.jobId === this.jobId).length < 1);
        this.destroy(false, common.errorCodeOrString(error, 'Cannot resolve job dependencies'));
        // Do not rethrow.
      });
  }

  /**
   * Assume slice is in state WORKDONE.
   * Create a READY clonedSlice with the necessary properties from slice.
   * Used in handleWorkReject.
   * @param {Slice} slice
   */
  cloneSlice (slice)
  {
    if (!slice.isWorkDone)
      throw new Error(`Can only clone a slice in state WORKDONE; slice is in state ${slice.state}`);
    // Clone a READY slice.
    const clonedSlice = slice.cloneReady();
    // Take the original slice out of play.
    common.removeElement(this.sliceInventory, slice);
    // Place it in inventory.
    this.sliceInventory.push(clonedSlice);
  }

  /** XXXpfr @todo Factor out statistics and EMA into another class. */
  /**
   * @param {number} x
   * @param {boolean} [isDensity]
   * @returns {boolean}
   */
  static isValid (x, isDensity = false)
  {
    return Number.isFinite(x) && !(x < 0) && !(isDensity && x > 1);
  }

  /**
   * Old legacy DCP schedulers can send a corrupt this.metrics .
   * We should get rid of this when we don't support old legacy DCP schedulers.
   */
  fixUpMetrics ()
  {
    if (!this.metrics)
      this.jobMessage.metrics = { sliceCPUTime: 0, sliceCPUDensity: 1, sliceGPUTime: 0, sliceGPUDensity: 0, sliceInDataSize: 0, sliceOutDataSize: 0 };
    else
    {
      if (!(JobManager.isValid(this.metrics.sliceCPUTime) && JobManager.isValid(this.metrics.sliceCPUDensity, true)))
      {
        this.metrics.sliceCPUTime = 1;
        this.metrics.sliceCPUDensity = 0.5;
      }
      if (!(JobManager.isValid(this.metrics.sliceGPUTime) && JobManager.isValid(this.metrics.sliceGPUDensity, true)))
      {
        this.metrics.sliceGPUTime = 1;
        this.metrics.sliceGPUDensity = 0.5;
      }
      if (!JobManager.isValid(this.metrics.sliceInDataSize))
        this.metrics.sliceInDataSize = 0;
      if (!JobManager.isValid(this.metrics.sliceOutDataSize))
        this.metrics.sliceOutDataSize = 0;
    }
  }

  /**
   * @param {Slice}         slice
   * @param {SandboxHandle} sandboxHandle
   * @returns {{CPUTime: number, GPUTime: number, CPUDensity: number, GPUDensity: number, InDataSize: number, OutDataSize: number}}
   */
  updateStatistics (slice, sandboxHandle)
  {
    /**
     * @param {{CPUTime: number, GPUTime: number, CPUDensity: number, GPUDensity: number, InDataSize: number, OutDataSize: number}} metrics
     * @returns {boolean}
     */
    const isValidMetrics = (metrics) => {
      return !debugBuild // Only check when debugBuild.
        || (JobManager.isValid(metrics.CPUTime) && JobManager.isValid(metrics.CPUDensity, true)
        && JobManager.isValid(metrics.GPUTime) && JobManager.isValid(metrics.GPUDensity, true)
        && JobManager.isValid(metrics.InDataSize) && JobManager.isValid(metrics.OutDataSize));
    }
    /** @returns {boolean} */
    const isValidLocalMetrics = () => {
      return !debugBuild // Only check when debugBuild.
        || (JobManager.isValid(this.localMetrics.CPUTime) && JobManager.isValid(this.localMetrics.CPUDensity, true)
        && JobManager.isValid(this.localMetrics.GPUTime) && JobManager.isValid(this.localMetrics.GPUDensity, true)
        && JobManager.isValid(this.localMetrics.CPUTime_variance) && JobManager.isValid(this.localMetrics.GPUTime_variance));
    }

    const timeReport = slice.timeReport;
    const dataReport = slice.dataReport;
    selectiveDebug() && console.debug('updateStatistics', slice.identifier, timeReport, dataReport);

    // Sanity
    if (!(timeReport.CPU >= 1))
    {
      timeReport.CPU = 1;
      timeReport.total += 1;
    }
    if (!(timeReport.total >= 1))
      timeReport.total = 1;

    const GPUTime = timeReport.webGPU + timeReport.webGL;
    if (this.hasGPU)
      this.supervisor.quantizeGPUExecution(slice, GPUTime);
    const metrics = {
      CPUTime:     timeReport.CPU,
      GPUTime,
      CPUDensity:  timeReport.CPU / timeReport.total,
      GPUDensity:  GPUTime / timeReport.total,
      InDataSize:  dataReport.InDataSize,
      OutDataSize: dataReport.OutDataSize,
    };
    slice.metrics = metrics;

    // Create the measurement object which will be passed to the metrics event.
    const eventMeasurements = {
      elapsed: timeReport.total / 1000,
      CPU:     metrics.CPUTime / 1000,
      GPU:     metrics.GPUTime / 1000,
      in:      metrics.InDataSize,
      out:     metrics.OutDataSize,
    };

    // https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
    // Emit metrics event for both jobHandle and sandboxHandle
    this.jobHandle.emit('metrics', slice.sliceNumber, eventMeasurements);
    sandboxHandle.emit( 'metrics', slice.sliceNumber, eventMeasurements);
    selectiveDebug2() && console.debug(`updateStatistics: slice ${slice.identifier}, eventMeasurements ${stringify(eventMeasurements)}`);

    if (!isValidMetrics(metrics))
      throw new Error(`JobManager.updateStatistics: metrics are in an inconsistent state ${utils.stringify(metrics)}`);

    if (!this.localMetrics)
      this.localMetrics = { CPUTime: 0, GPUTime: 0, ElapsedTime: 0, CPUDensity: 0, GPUDensity: 0, InDataSize: 0, OutDataSize: 0, CPUTime_variance: null, GPUTime_variance: null };

    // measuredSlices+1 because the current slice hasn't been measured yet in RS.updateMetrics.
    const alpha = this.metrics?.lastSliceNumber
      ? wd.constructAlpha(this.metrics.lastSliceNumber, this.metrics.measuredSlices + 1)
      : 0.5;
    selectiveDebug2() && console.debug(`updateStatistics: local alpha ${alpha}, global alpha ${this.metrics?.alpha}`);

    //
    // How to create EMA and EMVAR series from x[i], i = 0, 1, 2, ...
    // delta[i] <- x[i] - ema[i-1]
    // ema[i] <- ema[i-1] + alpha * delta[i]: ema[0] = x[0]
    // emvar[i] <- (1-alpha) * (emvar[i-1] + alpha * delta[i]^2): emvar[0] = 0
    //
    // Note: Must calculate this.localMetrics.CPUTime_variance first because
    //  this.localMetrics.CPUTime must be the previous value, not the current value.
    //  Similarly for this.localMetrics.GPUTime_variance.
    //

    this.localMetrics.CPUTime_variance = wd.nextEmvar(this.localMetrics.CPUTime_variance, this.localMetrics.CPUTime, metrics.CPUTime, alpha);
    this.localMetrics.GPUTime_variance = wd.nextEmvar(this.localMetrics.GPUTime_variance, this.localMetrics.GPUTime, metrics.GPUTime, alpha);
    this.localMetrics.CPUTime     = wd.nextEma(this.localMetrics.CPUTime,    metrics.CPUTime, alpha);
    this.localMetrics.GPUTime     = wd.nextEma(this.localMetrics.GPUTime,    metrics.GPUTime, alpha);
    this.localMetrics.ElapsedTime = wd.nextEma(this.localMetrics.ElapsedTime,  timeReport.total, alpha);
    this.localMetrics.CPUDensity  = wd.nextEma(this.localMetrics.CPUDensity, metrics.CPUDensity, alpha);
    this.localMetrics.GPUDensity  = wd.nextEma(this.localMetrics.GPUDensity, metrics.GPUDensity, alpha);
    this.localMetrics.InDataSize  = wd.nextEma(this.localMetrics.InDataSize, metrics.InDataSize, alpha);
    this.localMetrics.OutDataSize = wd.nextEma(this.localMetrics.OutDataSize, metrics.OutDataSize, alpha);

    if (displayStatHistory)
    {
      this.CPUHistory.push(this.localMetrics.CPUTime.toFixed(2));
      this.CPUVarHistory.push(Math.sqrt(this.localMetrics.CPUTime_variance).toFixed(3));
      console.log('CPUHistory', this.CPUHistory);
      console.log('CPUVarHistory', this.CPUVarHistory);
    }

    // SAVE
    // console.log('localMetrics:', this.localMetrics);
    // console.log('globalMetrics:', this.metrics);
    // console.log('jobHandleMetrics:', this.jobHandle.metrics);

    if (!isValidLocalMetrics())
      throw new Error(`JobManager.updateStatistics: localMetrics are in an inconsistent state ${utils.stringify(this.localMetrics)}`);

    selectiveDebug2() && console.debug('updateStatistics: emaCPUTime', this.emaCPUTime, 'estimateDensity', this.estimateDensity, 'alpha', alpha, 'metrics', metrics, 'g-metrics', this.metrics, 'l-metrics', this.localMetrics);
    return metrics;
  }

  /**
   * How JobManager.computeOverdueThreshold() works.
   * A slice is late, roughly, when it exceeds mean + 3 * stddev.
   * Using mean + 3 * stddev to predict overdue slices is extremely crude and error prone.
   * First off the slice execution time is not an IID process and even if it was, estimating mean
   * and variance from a low # of samples is highly error prone.  Suppose 3 slices have completed
   * for a job, then deciding if the 4th slice is late using mean + 3 * stddev can frequently give
   * false positives.  A good example of why slice execution time is not an IID process is emcoil.
   * It does numerical integration and when it gets in a region with high gradient it takes a lot longer;
   * it can vary from 1s to 50s and the large values are somewhat rare so there's a good chance of
   * missing them. I suppose we could model the slice price stochastic process as a Levi process
   * or Levi flight or as a jump diffusion which is roughly an Ito process composed with a
   * jump process, but that doesn't help in practice.
   * -----------------------------------------------------------------------------------------------------
   * Let's see here, JobManager.computeOverdueThreshold() does mean + 3 * stddev via
   *     mean = wall-clock-time = this.emaElapsedTime
   *     stddev^2 = Var(wall-clock-time) = Var(CPUTime + GPUTime) / (CPUDensity + GPUDensity)^2
   * and we've constructed
   *     mean   := this.emaElapsedTime
   *     stddev := Math.sqrt( wd.localElapsedVariance(this.localMetrics) )
   * The sandbox timing code returns { total, CPU, webGPU, webGL } where total is wall-clock time,
   * which is transformed and sent to RS, has:
   *   GPU = webGPU + webGL
   *   CPUTime = CPU
   *   CPUDensity = CPU / total
   *   GPUTime = GPU
   *   CPUDensity = GPU / total
   * Therefore
   *   wall-clock-time = this.emaElapsedTime = CPUTime / CPUDensity = (CPUTime + GPUTime) / (CPUDensity + GPUDensity)
   *   Var(wall-clock-time) = wd.localElapsedVariance(this.localMetrics) = Var(CPUTime + GPUTime) / (CPUDensity + GPUDensity)^2
   * because we're assuming Var(CPUDensity + GPUDensity) = 0. (edited) 
   * 
   * Calculate the overdue threshold for current job.
   * Does not apply to jobs in estimation.
   * @returns {number}
   */
  computeOverdueThreshold ()
  {
    selectiveDebug2() && console.debug(`JM.computeOverdueThreshold(${this.identifier},${this.supervisor.workerId})`, 'isEstimation', this.isEstimation, 'useLifetimeAnalysis', this.supervisor.options.useLifetimeAnalysis, 'soteriaFrequencyMs', this.supervisor.options.soteriaFrequencyMs);
    //this.supervisor.showSlices('JM.computeOverdueThreshold');
    if (this.isEstimation)
      return -1;
    const totalTime = this.emaElapsedTime;
    const totalVariance = wd.localElapsedVariance(this.localMetrics);
    // stddev is constrained:  stddev >= 0.125 * totalTime
    const stddev = Math.max(Math.sqrt(totalVariance), 0.125 * totalTime);
    // roughly Gaussian with mean = totalTime and stddev = soteriaConfig.sliceSigmas.
    const estimatedTime = totalTime + this.sliceSigmas * stddev;
    const threshold = this.sliceBiasMs + this.sliceMultiplier * estimatedTime;
    selectiveSupEx() && console.debug(`Jm.computeOverdueThreshold(${this.identifier},${this.supervisor.workerId}): threshold=${threshold * this.lifetimeFactor}/${threshold}/${this.lifetimeFactor}`, this.supervisor.options.soteriaFrequencyMs);

    return threshold * this.lifetimeFactor;
  }

  /**
   * Estimate the number of milliseconds of total execution time of the job.
   * @returns {number}
   */
  workRemaining ()
  {
    const adjustedTime = this.adjElapsedTime;
    const queuedTime = adjustedTime * this.queuedSlices.length;
    const workingSlices = this.workingSlices;
    let workingTime = 0;
    for (const slice of workingSlices)
    {
      workingTime += Math.max(0, adjustedTime - (Date.now() - slice.startTime));
      selectiveDebug2() && console.debug(`JobManager.workRemaining: adjustedTime ${adjustedTime}, slice-time-elapsed ${Date.now() - slice.startTime}`);
    }
    selectiveDebug() && console.debug(`JobManager.workRemaining: queuedTime ${queuedTime}, queuedCount ${this.queuedSlices.length}, workingTime ${workingTime}, workingCount ${this.workingSliceCount}`);
    return workingTime + queuedTime;
  }

  /**
   * NOTE: NOT PERFECT YET WRT CPU vs GPU DENSITY!!!!!!
   * Predict the total reduction in density of working sandboxes timeSpanMs from now.
   * This function is called right before fetchTask, in order to calculate how much space is available.
   * In pratice, the returned property queued will have length <= 1, because when it is more we won't call fetchTask.
   * @param {number} timeSpanMs
   * @returns {{ queued: Slice[], working: number }}
   */
  predictLoad (timeSpanMs)
  {
    const queuedSlices = this.queuedSlices;
    const workingSliceCount = this.workingSliceCount;
    const estimateDensity = this.estimateDensity;
    const emaElapsedTime = this.emaElapsedTime;
    let queuedCount = queuedSlices.length;
    selectiveDebug2() && console.debug(`JM.predictLoad(${timeSpanMs}): queued ${queuedCount}, workingSliceCount ${workingSliceCount}, density ${estimateDensity}, sliceTime ${emaElapsedTime}, estimation ${this.isEstimation}, long ${this.isLong}`);

    // Optimize to short-circuit when queued > 1, because we won't call fetchWork in that case.
    if (queuedCount > workingSliceCount + 1)
    {
      selectiveDebug2() && console.debug(`JM.predictLoad: bail early:`, queuedCount, workingSliceCount);
      return { queued: [null, null], working: workingSliceCount *  estimateDensity};
    }
    let space = 0;
    // 1) Predict the current working slices that will finish in timeSpanMs.
    // 2) Compensate for queued slices that may begin working.
    for (const slice of this.sliceInventory)
    {
      if (!slice.isReserved && !slice.isWorking)
        continue;
      const remainingTime = Math.max(0, emaElapsedTime - (Date.now() - slice.startTime));
      selectiveDebug2() && console.debug(`JM.predictLoad: predicting: queued ${queuedCount}, space ${space}, sliceTime ${emaElapsedTime}, remaining ${remainingTime}, branchTaken ${emaElapsedTime <= timeSpanMs - remainingTime}`);
      // If slice will end before timeSpanMs elapses, it may open up space.
      if (remainingTime <= timeSpanMs)
      {
        // Check to see whether a new queued slices can start running and finish in the remaining time.
        // Somewhat inaccurate because it's possible multiple slices could finish in the remaining time.
        // e.g. Suppose timeSpanMs = 5 * 1000 and emaElapsedTime is 1000, then there's room
        //      to successively schedule 5 slices, one after another.
        /** XXXpfr @todo Fix to incorporate successive scheduling. */
        if (queuedCount > 0)
        {
          --queuedCount;
          // If the new slice finishes in time, count it as opening up space.
          if (emaElapsedTime <= timeSpanMs - remainingTime)
            ++space;
        }
        else // No queued slice can run so space has opened up.
          ++space;
      }
    }
    // Optimize to short-circuit when queued > 1, because we won't call fetchWork in that case.
    let queued = [null, null];
    if (queuedCount < 2)
      queued = (queuedCount === 1) ? [queuedSlices[0]] : [];
    const working = (workingSliceCount - space) * estimateDensity;
    selectiveDebug() && console.debug(`JM.predictLoad done(queued=${queued.length},working=${working}): workingSliceCount ${workingSliceCount}, space ${space}, density ${estimateDensity}`);
    return { queued, working };
  }

  // _Idx
  //
  // reserveOneSlice, resetSlices, destroy, destroyAll
  //

  /**
   * Find one ready slice.
   * If there are no ready slices return undefined.
   * @returns {Slice}
   */
  readySlice ()
  {
    for (const slice of this.sliceInventory)
      if (slice.isReady)
        return slice;
    return null;
  }

  /**
   * Return the slice to the scheduler.
   * @param {Slice} slice
   * @param {string|{message: string|false, errorCode: string}} reason
   * @returns {Promise<*>}
   */
  returnSlice (slice, reason)
  {
    return this.supervisor.returnSlice(slice, reason);
  }

  /**
   * Destructor.
   * Warning: Please only call destroy(true) in supervisor.purgeJob.
   *
   * Never call this.state.destroy().
   * Because Supervisor.cursor contains arrays of job managers and it is too difficult to design the
   * cursor to easily remove individual job managers, so we just leave BROKEN or DEAD job managers
   * until the cursor algorithm removes them. They will be removed from Supervisor.sandboxInventory
   * after the next successful fetch by calling clearUnusedJobs, since BROKEN or DEAD means they
   * are or will be empty.
   *
   * @param {boolean} itsDeadJim - when true, places job in penultimate dead state.
   * @param {string|{errorCode: string, message: string|false}} reason
   * @returns {Promise<*>}
   */
  destroy (itsDeadJim, reason)
  {
    selectiveSupEx() && console.debug(`JobManager.destroy: terminating sandboxes and returning slices to scheduler for job manager ${this.identifier}, dieNow ${itsDeadJim}, reason:`, reason, this.sliceInventory.map(slice => slice.identifier));
    if (!this.dead)
    {
      if (itsDeadJim)
        this.state.set([ INITIAL, READY, STOP, REFUSE, BROKEN ], DEAD);
      else if (this.state.isNot(BROKEN))
        this.state.set([ INITIAL, READY, STOP, REFUSE ], BROKEN);
      // Normally looping over an array and deleting elements is error prone, but this case is
      // ok because sandboxes and this.supervisor.sandboxInventory are different arrays.
      const sandboxes = this.sandboxInventory;
      for (const sandbox of sandboxes)
        this.supervisor.returnSandbox(sandbox);
      /** @todo Generalize returnAllSlicesMessage to return all slices for a given job and worker (PFR) */
      const slicesToReturn = this.unFinishedSlices;
      return this.supervisor.returnSlices(slicesToReturn, reason)
        .finally(() => this.destroyHelper());
    }
  }

  /**
   * When we're shutting down all jobs, we return slices and sandboxes elsewhere.
   * Clean up stuff that might leak resources and emit 'flush' event.
   *
   * Never call this.state.destroy().
   * Please see the comment on the above function: destroy(itsDeadJim, reason)
   */
  destroyHelper ()
  {
    this.sliceInventory = [];
    this.state.removeAllListeners();
    this.jobHandle.emit('flush');
  }

  // _Idx
  //
  // resolveWorktime, runSliceOnSandbox
  //

  /**
   * Check to see if the job's worktime is supported by the dcp-client environment
   * and if so, choose the best matching version.
   * @returns {*}
   */
  resolveWorktime ()
  {
    if (!this.#worktimeResolved)
    {
      this.#worktimeResolved = true;
      // Resolves the semver range provided by the job to a specific version that the worker supports
      const worktimeVersions = [];
      for (let worktime of this.supervisor.worktimes)
        if (worktime.name === this.jobMessage.worktime.name)
          worktimeVersions.push(worktime.version);
      const newestSupportedWorktimeVersion = semver.maxSatisfying(worktimeVersions, this.jobMessage.worktime.version, true);
      if (!newestSupportedWorktimeVersion)
        throw new Error(`JobManager.resolveWorktime(${this.identifier}): Worker does not support ${this.jobMessage.worktime.name}@${this.jobMessage.worktime.version}`);
      this.jobMessage.worktime.version = newestSupportedWorktimeVersion;
    }
    return this.jobMessage;
  }

  /**
   * Execute slice on sandbox, collect results or handle errors, and clean up.
   *
   * Note: It is ok for an exception to escape runSliceOnSandbox because it is wrapped with a
   * promise-catch clause in Supervisor.#runSliceOnSandbox. However any such exception will cause
   * a fatal error to be emitted.  A fatal error is detected by
   *     worker.isWorkerError(error) === false
   * in a worker app and it is up to the worker app to act on it.  It could do nothing but report
   * the error, or it could call worker.stop(...) and clean up...
   *
   * @param {Slice}   slice   - The slice to execute on the sandbox.
   * @param {Sandbox} sandbox - The sandbox on which to execute the slice.
   * @returns {Promise<any>}
   */
  async runSliceOnSandbox (slice, sandbox)
  {
    selectiveDebug() && console.debug(`runSliceOnSandbox(${Date.now() - slice.startTime}): slice=${slice.identifier}, sandbox=${sandbox.identifier}`);

    if (this.supervisor.sliceTiming)
      slice['executionDelta'] = Date.now();
    slice.startTime = Date.now();

    // When evaluator goes down, all sandboxes are terminated.
    // Mark slice as READY so it can be picked up later with a different sandbox
    if (sandbox.isTerminated)
      return slice.resetFromReserved();
    // The sandbox may have been reused.
    sandbox.slice = slice;

    return sandbox.work()
      .finally(() => {
        if (this.supervisor.sliceTiming)
        {
          slice['executionDelta'] = Date.now() - slice['executionDelta'];
          slice['resultDelta'] = Date.now();
        }
      })
      .then((result) => {
        if (result === false)
        {
          // Not handling the early return from sandbox.work was a bad bug that caused recordResult
          // to throw with error code 'EUNCAUGHT'. So sometimes an unclean shutdown when the
          // evaluator's down could cause a job to be cancelled.
          selectiveDebug2() && console.debug(`runSliceOnSandbox: Early return from sandbox.work because sandbox ${sandbox.identifier} terminated`, sandbox.endEventFired);
          return this.returnSlice(slice, 'Early return from sandbox.work because sandbox terminated.');
        }

        selectiveDebug2() && console.debug(`runSliceOnSandbox: sandbox(endEventFired=${sandbox.endEventFired}): ${sandbox.identifier} - then-clause: ${slice.identifier}`, Boolean(result));

        if (!result)
        {
          if (!sandbox.endEventFired)
          {
            // Should never get here, but if this starts happening we need to fix it. The sandbox
            // has a good chance of already being reused to run another slice, which could be bad.
            // NOTE: if a workFn doesn't return anything, then result.result will be undefined,
            this.returnSlice(slice, `Slice ${slice.identifier} completed work, but there is no result.`);
            throw new DCPError(`Slice ${slice.identifier} completed work, but there is no result.`, 'NORESULT');
          }
          return this.returnSlice(slice, 'Bad result because evaluatorHandle end event has fired.');
        }

        // Record result.
        return this.supervisor.recordResult(slice, sandbox.sandboxHandle);
      }, (error) => {
        // Catches exception from sandbox.work.
        selectiveSupEx() && console.error(`runSliceOnSandbox: Failure executing slice on sandbox: ${sandbox.identifier}`, error);
        // Skip handling when the error is caused by an evaluatorHandle 'end' event.
        if (sandbox.endEventFired)
          return this.returnSlice(slice, 'Slice execution failed because evaluatorHandle end event has fired.');
        // Kills sandbox and either retries the slice or transitions slice to FAILED and returns to scheduler.
        this.supervisor.handleSandboxWorkError(sandbox, slice, error);
        this.supervisor.emit('sliceFailed');
      });
  }

  // _Idx
  //
  // removeSlice
  //

  /**
   * Remove slice from this.sliceInventory.
   * @param {Slice} slice - The slice to remove from this.sliceInventory.
   * @returns {boolean} - False when slice is not in this.sliceInventory.
   */
  removeSlice (slice)
  {
    selectiveDebug2() && console.debug(`removeSlice: ${slice.identifier}`);
    return common.removeElement(this.sliceInventory, slice);
  }

  // _Idx
  //
  // fetchError, fetchJob, fetchSliceData
  //

  /**
   * Throw fetch errors.
   * @param {Error}  error
   * @param {string} [kind]
   * @param {URL|string} [url]
   */
  fetchError (error, kind = 'artifacts', url)
  {
    if (false || selectiveSupEx())
    {
      let errorMsg = `Failed fetching ${kind} for job ${this.identifier}`;
      if (url)
      {
        if (typeof url === 'string')
          url = new URL(url);
        if (url.origin && url.origin !== 'null')
          errorMsg = `${errorMsg} from origin ${url.origin}`;
      }
      const errorCode = error['errorCode'] ?? error['code'];
      if (errorCode)
        errorMsg = `${errorMsg} with error code ${errorCode}`;
      errorMsg = `${errorMsg}; error message: ${error.message}`;
      this.supervisor.warning(errorMsg);
    }
    if (error['errorCode'] || error['code'] === 'EJOBFETCH' || error['code'] === 'ESLICEFETCH')
      throw error;
    if (error['code'] === 'EPERM_ORIGIN')
      throw new BadOriginError(error);
    throw new RemoteFetchError(error);
  }

  /**
   * Fetch work function, work function arguments and possibly the range object describing the jobs slices..
   * @param {object} mpe - messagePayloadElement: job object returned by task-jobs.js . 
   * @returns {Promise<*>}
   */
  async fetchJob (mpe)
  {
    debugging('supervisor') && utils.dumpObject(mpe, 'JobManager.fetchJob: mpe', 512);

    const promises = [];
    try
    {
      // Get workFn.
      if (!mpe.workFunction)
      {
        if (!mpe.codeLocation)
          throw new Error(`fetchJob(${this.identifier},${this.supervisor.workerId}): Invalid codeLocation; message payload: ${stringify(mpe)}`);
        const workFunctionPromise = this.fetchWorkFunctions(mpe.codeLocation)
          .then((workFunction) => {
            this.inputDataSize += workFunction.size;
            mpe.workFunction = workFunction.data;
            if (mpe.useStrict)
              mpe.useStrict  = true;
            delete mpe.codeLocation;
          }, (error) => {
            this.fetchError(error, 'workfunction', mpe.codeLocation);
          });
          promises.push(workFunctionPromise);
      }

      if (!mpe.mro && mpe.MROLocation)
      {
        const mroPromise = this.fetchData(mpe.MROLocation)
          .then((mro) => {
            // It's not ready for computing the inputDataSize yet -- that's done in JobManager.fetchSliceData .
            mpe.mro = mro.data;
            delete mpe.MROLocation;
          }, (error) => {
            this.fetchError(error, 'mro slice input', mpe.MROLocation);
          });
        promises.push(mroPromise)
      }

      // Get workFn args.
      if (!mpe.arguments && mpe.argumentsLocation)
      {
        mpe.arguments = new Array(mpe.argumentsLocation.length);
        for (let k = 0; k < mpe.argumentsLocation.length; k++)
        {
          if (!mpe.argumentsLocation[k].value)
            throw new Error(`fetchJob(${this.identifier},${this.supervisor.workerId}): Invalid argumentsLocation[${k}].value; message payload: ${stringify(mpe)}`);
          promises.push(this.fetchArguments(mpe.argumentsLocation[k].value)
            .then((arg) => {
              this.inputDataSize += arg.size;
              mpe.arguments[mpe.argumentsLocation[k].argIndex] = arg.data;
            }, (error) => {
              this.fetchError(error, 'arguments', mpe.argumentsLocation[k].value);
            })
          );
        }
      }

      return Promise.all(promises)
        .then(() => {
          if (mpe.argumentsLocation)
            delete mpe.argumentsLocation;
          return mpe;
        });
    }
    catch (error)
    {
      this.fetchError(error);
    }
  }

  /**
   * Look up slice.datumUri or use the range object this.mro, constructed by fetchJob.
   * @param {Slice} slice 
   * @returns {Promise<{ data: *, size: number }>}
   */
  async fetchSliceData (datumUri, slice)
  {
    try
    {
      if (!datumUri)
      {
        if (!this.mro)
          throw new Error('Must complete call to JobManager.fetchJob before calling JobManager.fetchSliceData.');
        /** XXXpfr @todo Inefficient, we're rehydrating the whole range for a single slice datum. */
        const ro = rehydrateRange(this.mro);
        // Slice numbers start at 1.
        const sliceDatum = ro[slice.sliceNumber - 1];
        debugging('supervisor') && console.debug(`Fetched mro datum: ${stringify(sliceDatum)}`);
        return { data: sliceDatum, size: JSON.stringify(sliceDatum).length };
      }

      if (!datumUri)
        throw new Error(`fetchSliceData(${slice.identifier},${this.identifier},${this.supervisor.workerId}): Invalid datumUri ${stringify(datumUri)}`);
      return this.fetchData(datumUri)
        .catch((error) => {
          this.fetchError(error, 'slice input', datumUri);
        });
    }
    catch (error)
    {
      this.fetchError(error);
    }
  }

  // _Idx
  //
  // countSliceStr, fetchThing, dumpSlices
  //

  /**
   * Debugging helper.
   * @param {string}  [tag='']
   * @returns {string}
   */
  countSliceStr (tag)
  {
    const { unassigned, ready, reserved, working, workdone, complete, failed, finished } = this.dumpSlices (tag, false, false);
    return `${tag}: u/r/rsv/w/wd/c/f/fsh ${unassigned}/${ready}/${reserved}/${working}/${workdone}/${complete}/${failed}/${finished}`;
  }

  /**
   * @param {URL|string} uri
   * @param {string} purpose
   * @returns {Promise<*>}
   */
  async fetchThing(uri, purpose, key)
  {
    const url = new URL(uri);
    if (!url.origin) // data uri -> treat as infallible 
      return fetchURIAndLength(uri, this.originManager, purpose);

    const urlSegment = url.origin + '/' + url.pathname.split('/')[1];
    const delay = this.supervisor.fetchBackoffs[urlSegment];
    if (delay && delay.lastErrorTime + delay.wait*1000 > Date.now())
    {
      this.fetchFailure(purpose);
      return {};
    }

    try
    {
      //this.injectError(purpose, allowOriginsPurposes.fetchWorkFunctions); // SAVE
      const res = await fetchURIAndLength(uri, this.originManager, purpose, key);
      if (delay)
        delete this.supervisor.fetchBackoffs[urlSegment];
      return res;
    }
    catch (error)
    {
      if (!error.code || !error.code?.startsWith('EHTTP_STATUS_'))
        throw error;
      // Treat any error from fetching data as transient & willing to retry.
      // Destroy the job manager to return slices, but be willing to accept
      // and try again later.
      if (delay)
      {
        delay.lastErrorTime = Date.now()
        delay.wait = delay.backoff.next();
      }
      else
      {
        const backoff = new utils.Backoff({ mult: 2, base: 2, max: 180 });
        this.supervisor.fetchBackoffs[urlSegment] = {
          lastErrorTime: Date.now(),
          wait: backoff.next(),
          backoff,
        };
      }
      this.fetchFailure(purpose);
      return {};
    }
  }

  // /** testing SAVE */
  // injectError (purpose, purposeTarget, errorTimespan = 30 * 1000)
  // {
  //   if (purpose === purposeTarget)
  //   {
  //     if (!this.injectError['t0'])
  //       this.injectError['t0'] = Date.now();
  //     if (Date.now() - this.injectError['t0'] < errorTimespan)
  //     {
  //       console.log(`${purpose} goes BOOM!`);
  //       throw new Error('BOOM!');
  //     }
  //   }
  // }

  /**
   * Handle different fetch artifacts failure modes.
   * @param {string} purpose
   */
  fetchFailure (purpose)
  {
    if (purpose === allowOriginsPurposes.fetchData)
      throw new DCPError(`Failed to fetch slice data for job ${this.identifier}`, 'ESLICEFETCH');
    throw new DCPError(`Failed to fetch job artifacts data for job ${this.identifier}`, 'EJOBFETCH');
  }

  /**
   * @param {URL|string} uri
   * @returns {Promise<*>}
   */
  async fetchData (uri) { return this.fetchThing(uri, allowOriginsPurposes.fetchData, this.unWrapCGId()); }
  /**
   * @param {URL|string} uri
   * @returns {Promise<*>}
   */
  async fetchArguments (uri) { return this.fetchThing(uri, allowOriginsPurposes.fetchArguments, this.unWrapCGId()); }
  /**
   * @param {URL|string} uri
   * @returns {Promise<*>}
   */
  async fetchWorkFunctions (uri) { return this.fetchThing(uri, allowOriginsPurposes.fetchWorkFunctions, this.unWrapCGId()); }

  /**
   * jobId-CGId --> CGId
   */
  unWrapCGId ()
  {
    const idArr = this.jobCgId.split('-');
    const cgId = idArr[idArr.length - 1];
    return cgId;
  }

  /**
   * Debugging helper.
   * @param {string}  [tag='']
   * @param {boolean} [details=false]
   * @param {boolean} [display=true]
   * @returns {{ ready, reserved, working, workdone, complete, failed, finished, unassigned }}
   */
  dumpSlices (tag = '', details = false, display = true)
  {
    if (display)
      console.log(`dumpSlices(${tag}): ${this.identifier}, sliceCount ${this.sliceInventory.length}, sandboxCount ${this.sandboxInventory.length}`, Date.now() - this.supervisor.lastTime);
    if (this.sliceInventory.length < 1) return { unassigned:0, ready:0, reserved:0, working:0, workdone:0, complete:0, failed:0, finished:0 };
    if (details)
    {
      // Detailed classification of slices.
      const unassigned = [], ready = [], reserved = [], working = [], workdone = [], complete = [], failed = [], finished = [];
      for (const slice of this.sliceInventory)
      {
        if (slice.isReady)
          ready.push(slice);
        else if (slice.isReserved)
          reserved.push(slice);
        else if (slice.isWorking)
          working.push(slice);
        else if (slice.isWorkDone)
          workdone.push(slice);
        else if (slice.isComplete)
          complete.push(slice);
        else if (slice.isFailed)
          failed.push(slice);
        else if (slice.isFinished)
          finished.push(slice);
        else if (slice.isUnassigned)
          unassigned.push(slice);
        else
          throw new Error(`dumpSlices: Unexpected kind a slice; ${slice.identifier}`);
      }
      if (display)
      {
        const dmpSlices = (name, slices) => {
          console.log(`-----${name}(${slices.length})-----------------------------------------------------------------`);
          for (const slice of slices)
            console.log(slice.identifier);
        }
        dmpSlices('unassigned', unassigned);
        dmpSlices('ready',      ready);
        dmpSlices('reserved',   reserved);
        dmpSlices('working',    working);
        dmpSlices('workdone',   workdone);
        dmpSlices('complete',   complete);
        dmpSlices('failed',     failed);
        dmpSlices('finished',   finished);
        console.log('-----------------------------------------------------------------------------------');
      }
      return {
        ready:      ready.length,
        reserved:   reserved.length,
        working:    working.length,
        workdone:   workdone.length,
        complete:   complete.length,
        failed:     failed.length,
        finished:   finished.length,
        unassigned: unassigned.length,
      };
    }
    else
    {
      // Quick classification of slices.
      const { ready,
              reserved,
              working,
              workdone,
              complete,
              failed,
              finished,
              unassigned } = common.sliceCounts(this.sliceInventory);
      if (display)
      {
        const dumpCount = (name, count) => {
          console.log(`-----${name}(${count})-----------------------------------------------------------------`);
        }
        dumpCount('unassigned', unassigned);
        dumpCount('ready',      ready);
        dumpCount('reserved',   reserved);
        dumpCount('working',    working);
        dumpCount('workdone',   workdone);
        dumpCount('complete',   complete);
        dumpCount('failed',     failed);
        dumpCount('finished',   finished);
        console.log('-----------------------------------------------------------------------------------');
      }
      return { ready, reserved, working, workdone, complete, failed, finished, unassigned };
    }
  }

  /**
   * Concise view of job state.
   */
  get jobState ()
  {
    // Count the different kinds of slices.
    const { unassigned, ready, reserved, working, workdone, complete, failed, finished } = this.dumpSlices ('', false, false);
    // Make a nice summary of the current state of the job.
    const state = {
      name:                this.public?.name ?? "<unknown>",
      jobId:               this.jobId,
      state:               this.state.toString(),
      estimation:          this.isEstimation,
      long:                this.isLong,
      inputDataSize:       this.inputDataSize,
      almostDone:          this.almostDone,
      cpuTime:             this.emaCPUTime,
      gpuTime:             this.emaGPUTime,
      cpuDensity:          this.estimateCPUDensity,
      gpuDensity:          this.estimateGPUDensity,
      hasGPU:              this.hasGPU,
      sandboxes:           this.sandboxInventory.length,
      assdSandboxes:       this.assignedSandboxes.length,
      slices:              this.sliceInventory.length,
      queuedSlices:        this.queuedSlices.length,
      workingSlices:       this.workingSliceCount,
      workRemaining:       this.workRemaining,
      sliceDescrs:         `u/r/rsv/w/wd/c/f/fsh ${unassigned}/${ready}/${reserved}/${working}/${workdone}/${complete}/${failed}/${finished}`,
    };
    return state;
  }

}
exports.JobManager = JobManager;
